import { useEffect } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { ProgressWithValue } from "@/components/ui/progress-with-value"
import { Skeleton } from "@/components/ui/skeleton"
import { useTypedFeatureFlag } from "@/hooks/use-feature-flag"
import { cn } from "@/lib/utils"
import { TrendingUp } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { useSubscription } from "../hooks/use-subscription"
import { Plan } from "../types"

export function CreditUsageCard() {
  const showCreditUsage = useTypedFeatureFlag("show-credit-usage-card", false)
  const navigate = useNavigate()
  const { currentSubscription, plans, isLoading, error, actions } = useSubscription()

  useEffect(() => {
    if (!currentSubscription && !error) {
      actions.fetchBillingInfo()
    }
  }, [currentSubscription, actions, error])

  if (!showCreditUsage) {
    return null
  }

  const handleNavigateToPlans = () => {
    navigate("/settings/billing")
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-36 rounded-md" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <Skeleton className="mt-1 h-4 w-48 rounded-md" />
        </CardHeader>
        <CardContent className="space-y-2 pb-4">
          <Skeleton className="h-5 w-full rounded-md" />
          <Skeleton className="h-3.5 w-3/4 rounded-md" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-10 w-full rounded-md" />
        </CardFooter>
      </Card>
    )
  }

  if (error) {
    return
  }

  if (!currentSubscription || !plans || plans.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Consumo Indisponível</CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-muted-foreground text-sm">
            Dados de assinatura ou plano não disponíveis para exibir o consumo.
          </p>
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleNavigateToPlans}
            className="hover:bg-primary/90 w-full text-sm transition-colors duration-200"
          >
            Ver planos
          </Button>
        </CardFooter>
      </Card>
    )
  }
  if (["canceled", "inactive"].includes(currentSubscription.status)) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Consumo Indisponível</CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-muted-foreground text-sm">Sua assinatura está inativa ou cancelada.</p>
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleNavigateToPlans}
            className="hover:bg-primary/90 w-full text-sm transition-colors duration-200"
          >
            Ver planos
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const currentPlanDetails = plans.find((plan: Plan) => plan.id === currentSubscription.planId)
  const planName = currentPlanDetails?.name || "Plano desconhecido"

  const includedCredits = currentSubscription.includedCredits || 0
  const usedCredits = Math.max(0, includedCredits - (currentSubscription.remainingCredits || 0))
  const progressValue =
    includedCredits > 0 ? Math.min(100, (usedCredits / includedCredits) * 100) : 0

  const creditsLabel = () =>
    `${usedCredits.toLocaleString("pt-BR")} / ${includedCredits.toLocaleString("pt-BR")} contatos`

  const progressBarColor =
    progressValue > 85 ? "bg-red-500" : progressValue > 60 ? "bg-yellow-500" : "bg-blue-500"

  return (
    <Card className="shadow-sm transition-shadow duration-200 ease-in-out hover:shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base font-semibold">Consumo do Período</CardTitle>
          <Badge
            variant={"secondary"}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-all duration-200 hover:opacity-90"
            )}
          >
            {planName}
          </Badge>
        </div>
        <CardDescription className="text-xs">
          De{" "}
          {new Date(currentSubscription.currentPeriodStart).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "short",
          })}{" "}
          a{" "}
          {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "short",
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5 pb-3">
        <ProgressWithValue
          label={creditsLabel}
          value={progressValue}
          position="end"
          className="h-5 rounded-full bg-gray-500"
          valueClassName="text-xs font-medium top-[2.5px] text-white mix-blend-screen px-2"
          indicatorClassName={cn(
            "rounded-full transition-all duration-500 ease-out",
            progressBarColor
          )}
        />
        <p className="text-muted-foreground pt-0.5 text-xs">
          Contatos ativos que enviaram ao menos uma mensagem no período.
        </p>
      </CardContent>
      <CardFooter>
        <Button
          onClick={handleNavigateToPlans}
          className="hover:bg-primary/90 focus-visible:ring-primary/50 w-full text-sm font-medium transition-all duration-200 ease-in-out hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          <TrendingUp className="mr-2 h-4 w-4" />
          Mudar Plano
        </Button>
      </CardFooter>
    </Card>
  )
}
